import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthGuard } from '@/guards';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Plugs } from 'phosphor-react';
import StripeCheckoutModal from '@/components/dashboard/StripeCheckoutModal';
import { PlanComparisonTable } from '@/components/workspace/PlanComparisonTable';
import { LogoUpload } from '@/components/workspace/LogoUpload';
import { UserList } from '@/components/workspace/UserList';
import { StepIndicator } from '@/components/workspace/StepIndicator';
import { plansApi, type PlanResponse } from '@/api/plans';
import { workspacesApi } from '@/api/workspaces';

const PRO_PLAN_TYPE = 'pro';

export default function CreateWorkspacePage() {
  return (
    <AuthGuard>
      <CreateWorkspaceView />
    </AuthGuard>
  );
}

function CreateWorkspaceView() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { createWorkspace } = useWorkspace();
  const [currentStep, setCurrentStep] = useState(1);
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [planSubscribed, setPlanSubscribed] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [logoMiniFile, setLogoMiniFile] = useState<File | null>(null);
  const [logoMiniPreview, setLogoMiniPreview] = useState('');
  const [createdWorkspaceId, setCreatedWorkspaceId] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ email: string; role: 'member' | 'admin' }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStripeCheckoutOpen, setIsStripeCheckoutOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const totalSteps = 4;
  const proPlan = plans.find((p) => p.type === PRO_PLAN_TYPE);

  const displayName = user?.name || user?.email || 'Usuário';
  const initials = (displayName || 'U')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  useEffect(() => {
    plansApi.getAll().then(setPlans).catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    if (currentStep === 2) {
      const t = setTimeout(() => nameInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [currentStep]);

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!planSubscribed) {
        setError('Assine o plano para continuar.');
        return;
      }
      setError(null);
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      if (!workspaceName.trim()) {
        setError('Por favor, insira um nome para o workspace');
        return;
      }
      setIsSubmitting(true);
      setError(null);
      try {
        const created = await createWorkspace(workspaceName.trim());
        setCreatedWorkspaceId(created.id);
        if (logoMiniFile) {
          await workspacesApi.uploadAvatar(created.id, logoMiniFile);
        }
        setCurrentStep(3);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao criar workspace';
        setError(message);
        toast.error('Erro', message);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (currentStep === 3) {
      setCurrentStep(4);
      return;
    }

    if (currentStep === 4) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || !createdWorkspaceId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (planSubscribed && users.length > 0) {
        for (const u of users) {
          await workspacesApi.createInvitation(createdWorkspaceId, {
            email: u.email,
            role: u.role,
          });
        }
        toast.success('Convites enviados', `${users.length} convite(s) enviado(s) com sucesso.`);
      }
      toast.success('Workspace criado', 'Seu workspace foi configurado com sucesso!');
      navigate('/dashboard');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar convites';
      setError(message);
      toast.error('Erro', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canGoNextStep1 = planSubscribed;
  const isStep2Ready = workspaceName.trim().length > 0;

  return (
    <div className="relative min-h-screen bg-[color:var(--background)] text-[color:var(--foreground)]">
      <header className="fixed top-0 left-0 right-0 z-20 px-6 py-4 max-md:px-4 max-md:py-3">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <Link to="/dashboard" className="mr-auto w-[135px]">
            <img alt="Logo" width={135} height={36} src="/logo.svg" className="w-full dark:hidden block" />
            <img alt="Logo" width={135} height={36} src="/logo-dark.svg" className="w-full dark:block hidden" />
          </Link>
          <button
            type="button"
            className="relative flex size-11 rounded-full border-2 border-[color:color-mix(in_oklab,var(--card-border)_70%,transparent)] bg-[color:color-mix(in_oklab,var(--card-bg)_82%,transparent)]"
            aria-label="Usuário"
            title={displayName}
            disabled
          >
            <span className="m-[2px] inline-flex h-[calc(100%-4px)] w-[calc(100%-4px)] items-center justify-center rounded-full bg-[color:var(--background)] text-xs font-semibold text-[color:var(--foreground)]">
              {initials || 'U'}
            </span>
          </button>
        </div>
      </header>

      <main className="relative z-0 pt-24 pb-28 px-6 max-md:px-4 max-md:pt-20 max-md:pb-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl font-bold tracking-tight max-md:text-2xl mb-2">
                  {currentStep === 1 && 'Plano'}
                  {currentStep === 2 && 'Workspace'}
                  {currentStep === 3 && 'Integrações'}
                  {currentStep === 4 && 'Convidar membros'}
                </h1>
                <p className="text-base theme-text-muted">
                  {currentStep === 1 && 'Assine o plano para desbloquear recursos e poder convidar membros.'}
                  {currentStep === 2 && 'Defina o nome e a logo do seu workspace.'}
                  {currentStep === 3 && 'Conecte integrações ao workspace (opcional).'}
                  {currentStep === 4 && 'Convite membros. Os convites só são enviados após o plano assinado.'}
                </p>
              </div>

              <div className="min-h-[400px]">
                <AnimatePresence mode="wait">
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <PlanComparisonTable
                        onSubscribe={() => setIsStripeCheckoutOpen(true)}
                      />
                      {planSubscribed && (
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                          Plano assinado. Clique em Próximo para continuar.
                        </p>
                      )}
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div className="space-y-2">
                        <label className="text-sm font-medium theme-text">Nome do Workspace</label>
                        <input
                          ref={nameInputRef}
                          type="text"
                          value={workspaceName}
                          onChange={(e) => {
                            setWorkspaceName(e.target.value);
                            setError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && isStep2Ready) handleNext();
                          }}
                          placeholder="Ex: Minha Empresa"
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-[color:var(--nt-primary)] focus:ring-2 focus:ring-[color:var(--nt-primary)]/40 transition-all duration-200 theme-input theme-text placeholder:text-[color:var(--foreground-muted)]/60 placeholder:text-sm text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium theme-text">Logo (mini)</label>
                        <LogoUpload
                          label="Logo mini"
                          preview={logoMiniPreview}
                          file={logoMiniFile}
                          onFileChange={(file) => {
                            setLogoMiniFile(file);
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => setLogoMiniPreview(reader.result as string);
                              reader.readAsDataURL(file);
                            } else {
                              setLogoMiniPreview('');
                            }
                          }}
                          defaultImage="/agent-logo.svg"
                        />
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div className="rounded-xl border theme-border p-6 bg-[color:color-mix(in_oklab,var(--card-bg)_50%,transparent)]">
                        <div className="flex items-center gap-3 mb-4">
                          <Plugs className="size-6 text-[color:var(--nt-primary)]" weight="duotone" />
                          <h3 className="text-lg font-semibold theme-text">Integrações disponíveis</h3>
                        </div>
                        <p className="text-sm theme-text-muted mb-4">
                          Conecte ferramentas ao seu workspace. Você pode configurar integrações depois nas configurações.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <span className="px-3 py-2 rounded-lg border theme-border text-sm theme-text-muted bg-[color:color-mix(in_oklab,var(--card-bg)_70%,transparent)]">
                            ClickUp (em breve)
                          </span>
                          <span className="px-3 py-2 rounded-lg border theme-border text-sm theme-text-muted bg-[color:color-mix(in_oklab,var(--card-bg)_70%,transparent)]">
                            Outras em breve
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 4 && (
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      {!planSubscribed && (
                        <p className="text-sm theme-text-muted p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          Os convites só são enviados após o plano estar assinado (etapa 1). Você pode concluir sem convidar agora e convidar depois.
                        </p>
                      )}
                      <UserList
                        users={users}
                        onAddUser={(email, role) => {
                          setUsers([...users, { email, role }]);
                          setError(null);
                        }}
                        onRemoveUser={(email) => {
                          setUsers(users.filter((u) => u.email !== email));
                        }}
                        onError={(err) => setError(err ?? null)}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {error && (
                  <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mt-8 pt-6 border-t theme-border">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="h-10 px-4 text-sm theme-text-muted hover:theme-text transition-colors"
                >
                  Pular
                </button>
                <div className="flex items-center gap-3">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="h-10 px-4 rounded-lg border theme-border theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_88%,transparent)] transition-colors flex items-center gap-2 text-sm font-semibold"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Voltar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleNext()}
                    disabled={
                      isSubmitting ||
                      (currentStep === 1 && !canGoNextStep1) ||
                      (currentStep === 2 && !isStep2Ready)
                    }
                    className="h-10 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-60 bg-[color:var(--nt-primary)] text-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 flex items-center gap-2"
                  >
                    {currentStep === totalSteps ? (
                      isSubmitting ? 'Enviando...' : 'Concluir'
                    ) : (
                      <>
                        Próximo
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <StepIndicator
                steps={[
                  {
                    step: 1,
                    title: 'Plano',
                    description: 'Assinar o plano',
                    completed: currentStep > 1,
                    current: currentStep === 1,
                  },
                  {
                    step: 2,
                    title: 'Workspace',
                    description: 'Nome e logo mini',
                    completed: currentStep > 2,
                    current: currentStep === 2,
                  },
                  {
                    step: 3,
                    title: 'Integrações',
                    description: 'Conectar integrações',
                    completed: currentStep > 3,
                    current: currentStep === 3,
                  },
                  {
                    step: 4,
                    title: 'Convidar membros',
                    description: 'Envio após plano assinado',
                    completed: currentStep > 4,
                    current: currentStep === 4,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </main>

      <StripeCheckoutModal
        isOpen={isStripeCheckoutOpen}
        onClose={() => setIsStripeCheckoutOpen(false)}
        amount={proPlan?.priceInCents ?? 8990}
        planName={proPlan ? `${proPlan.name} - R$ ${(proPlan.priceInCents / 100).toFixed(2).replace('.', ',')}/mês` : 'Paladino Pro - R$ 89,90/mês'}
        planId={proPlan?.id}
        onSuccess={() => {
          setPlanSubscribed(true);
          setError(null);
          setIsStripeCheckoutOpen(false);
        }}
      />
    </div>
  );
}
